import React, { useState } from "react";
import {
  LuFileText,
  LuDownload,
  LuSearch,
  LuCircleAlert,
  LuBuilding,
  LuUsers,
  LuBriefcase,
  LuShieldCheck,
  LuLandmark,
  LuGlobe,
  LuCheckCircle,
  LuSparkles
} from "react-icons/lu";
import { FaFilePdf, FaFileWord, FaTimes } from "react-icons/fa";
import PageHeader from "../../components/PageHeader";
import { useTheme } from "../../context/SocketContext";
import { toast } from "../../components/ui/Toast";
import { usePortalContent, buildPortalFileUrl } from "../../portal";

const CATEGORY_ICON_MAP = {
  "Business & Company Agreements": LuBuilding,
  "Employment & Workforce Templates": LuUsers,
  "Sales, Commercial & Service Agreements": LuBriefcase,
  "Confidentiality & Intellectual Property": LuShieldCheck,
  "Corporate & Financial Documents": LuLandmark,
  "Website & Online Business Documents": LuGlobe,
};

const getCategoryIcon = (categoryName) => {
  return CATEGORY_ICON_MAP[categoryName] || LuBuilding;
};

const TEMPLATE_CATEGORIES = [
  {
    category: "Business & Company Agreements",
    icon: LuBuilding,
    templates: [
      {
        id: "operating-agreement",
        title: "Operating Agreement",
        description:
          "An Operating Agreement defines the ownership structure, management, rights, responsibilities, and operating procedures of an LLC. It provides members with a framework for managing the company and addressing important business situations."
      },
      {
        id: "partnership-agreement",
        title: "Partnership Agreement",
        description:
          "A Partnership Agreement establishes the relationship between business partners and outlines their respective contributions, responsibilities, ownership interests, profit-sharing arrangements, and decision-making rights."
      },
      {
        id: "founder-agreement",
        title: "Founder Agreement",
        description:
          "A Founder Agreement helps co-founders establish clear expectations from the beginning of a business. It can address founder roles, responsibilities, ownership, decision-making, intellectual property, and equity vesting arrangements."
      },
      {
        id: "joint-venture-agreement",
        title: "Joint Venture Agreement",
        description:
          "A Joint Venture Agreement establishes the terms under which two or more businesses collaborate on a specific business activity or venture. It can define each party's contributions, responsibilities, ownership, profit-sharing, and obligations."
      },
      {
        id: "collaboration-agreement",
        title: "Collaboration Agreement",
        description:
          "A Collaboration Agreement outlines the terms under which two or more parties work together on a business, product, project, or other initiative. It can clarify responsibilities, contributions, intellectual property rights, and how revenue or profits may be shared."
      },
      {
        id: "memorandum-of-understanding",
        title: "Memorandum of Understanding (MOU)",
        description:
          "An MOU documents the understanding and expectations between two or more parties who intend to work together toward a common objective. It is often used as an initial framework before entering into a more detailed formal agreement."
      },
      {
        id: "letter-of-intent",
        title: "Letter of Intent for a Business Venture",
        description:
          "A Letter of Intent (LOI) communicates the parties' intention to pursue a proposed business relationship or transaction. It generally outlines key commercial terms and provides a framework for further negotiations."
      }
    ]
  },
  {
    category: "Employment & Workforce Templates",
    icon: LuUsers,
    templates: [
      {
        id: "employment-agreement",
        title: "Employment Agreement",
        description:
          "An Employment Agreement establishes the terms and conditions of an employment relationship, including compensation, benefits, working hours, responsibilities, confidentiality, and other applicable obligations."
      },
      {
        id: "employment-offer-letter",
        title: "Employment Offer Letter",
        description:
          "An Employment Offer Letter communicates the key terms of a job offer, such as position, compensation, start date, and other basic employment conditions."
      },
      {
        id: "independent-contractor-agreement",
        title: "Independent Contractor Agreement",
        description:
          "An Independent Contractor Agreement defines the relationship between a business and an independent contractor or consultant. It helps clarify the scope of services, compensation, responsibilities, confidentiality, intellectual property, and other important terms."
      },
      {
        id: "consulting-agreement",
        title: "Consulting Agreement",
        description:
          "A Consulting Agreement establishes the relationship between a consultant and a client and defines the services to be provided, fees, project duration, responsibilities, confidentiality, and other applicable terms."
      },
      {
        id: "non-compete-agreement",
        title: "Non-Compete Agreement",
        description:
          "A Non-Compete Agreement may establish restrictions concerning certain competitive activities by an employee or other party. The enforceability and permitted scope of non-compete provisions can vary significantly depending on applicable law and jurisdiction."
      },
      {
        id: "intellectual-property-assignment",
        title: "Intellectual Property Assignment Agreement",
        description:
          "An Intellectual Property Assignment Agreement helps establish the transfer or assignment of intellectual property rights in eligible work, inventions, designs, content, software, or other materials created by an employee or contractor for a business."
      },
      {
        id: "employee-termination-letter",
        title: "Employee Termination Letter",
        description:
          "An Employee Termination Letter formally communicates the end of an employment relationship and may outline the effective termination date, final procedures, return of company property, and other relevant information."
      }
    ]
  },
  {
    category: "Customer & Commercial Agreements",
    icon: LuBriefcase,
    templates: [
      {
        id: "service-agreement",
        title: "Service Agreement",
        description:
          "A Service Agreement establishes the terms between a service provider and a client. It can define the scope of services, fees, payment terms, responsibilities, timelines, deliverables, and other conditions of the engagement."
      },
      {
        id: "sales-agreement",
        title: "Sales Agreement",
        description:
          "A Sales Agreement sets out the terms of a sale between a buyer and seller. It can specify the products or services being sold, pricing, payment terms, delivery requirements, warranties, and other applicable conditions."
      },
      {
        id: "bill-of-sale",
        title: "Bill of Sale",
        description:
          "A Bill of Sale documents the transfer of ownership of an item or asset from a seller to a buyer. It can serve as evidence of the transaction and the transfer of the relevant property."
      },
      {
        id: "release-of-liability",
        title: "Release of Liability",
        description:
          "A Release of Liability is used when one party agrees to release another party or organization from specified claims or liabilities, subject to the terms and limitations of the document and applicable law."
      },
      {
        id: "demand-letter",
        title: "Demand Letter",
        description:
          "A Demand Letter is a formal written communication requesting payment, performance, or another specific action from an individual or business. It may be used to attempt to resolve a dispute before pursuing further legal action."
      }
    ]
  },
  {
    category: "Confidentiality & Intellectual Property",
    icon: LuShieldCheck,
    templates: [
      {
        id: "non-disclosure-agreement",
        title: "Non-Disclosure Agreement (NDA)",
        description:
          "A Non-Disclosure Agreement helps protect confidential or proprietary information shared between parties. It establishes obligations regarding the use, protection, and disclosure of specified confidential information."
      },
      {
        id: "website-linking-agreement",
        title: "Website Linking Agreement",
        description:
          "A Website Linking Agreement establishes the terms under which one party may link to another party's website and, where applicable, use specified trademarks or branding in connection with that link."
      },
      {
        id: "cease-and-desist-letter",
        title: "Cease & Desist Letter – Trademark Infringement",
        description:
          "A Cease & Desist Letter can be used to formally request that an individual or business stop conduct that may infringe upon or misuse a trademark or other protected rights."
      }
    ]
  },
  {
    category: "Corporate & Governance Documents",
    icon: LuLandmark,
    templates: [
      {
        id: "general-corporate-resolution",
        title: "General Corporate Resolution",
        description:
          "A Corporate Resolution records a formal decision or authorization made by a corporation's board of directors, shareholders, or other authorized parties. It can be used to document approval for specific corporate actions."
      },
      {
        id: "organization-meeting-minutes",
        title: "Organization Meeting Minutes",
        description:
          "Organization Meeting Minutes provide an official record of discussions, decisions, approvals, and actions taken during formal corporate meetings, including organizational, board, or shareholder meetings."
      },
      {
        id: "83b-election-form",
        title: "83(b) Election Form",
        description:
          "An 83(b) Election relates to certain restricted property or equity received in connection with services and is used to notify the IRS that the taxpayer elects to include the applicable value in income at the time of transfer, subject to applicable tax rules and eligibility requirements.",
        note:
          "This filing has specific IRS requirements and deadlines and should be reviewed carefully with a qualified tax professional or attorney."
      }
    ]
  },
  {
    category: "Website & Online Business Documents",
    icon: LuGlobe,
    templates: [
      {
        id: "terms-of-service",
        title: "Terms of Service",
        description:
          "Terms of Service establish the rules and conditions governing a user's access to and use of a website, application, or online service. They may address acceptable use, user responsibilities, intellectual property, limitations of liability, termination, and other important terms."
      },
      {
        id: "privacy-policy",
        title: "Privacy Policy",
        description:
          "A Privacy Policy explains how a website or business collects, uses, stores, shares, and protects personal information. Privacy requirements can vary depending on the business, users, location, industry, and applicable laws, so the template should be appropriately customized."
      }
    ]
  }
];

const BusinessContractTemplates = () => {
  const { currentThemeColors } = useTheme();
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const { content: portalContent } = usePortalContent('business-contract-templates');

  const rawCategories =
    Array.isArray(portalContent?.categories) && portalContent.categories.length > 0
      ? portalContent.categories
      : TEMPLATE_CATEGORIES;

  const resolvedCategories = rawCategories.map((cat) => ({
    ...cat,
    icon: cat.icon || getCategoryIcon(cat.category),
    templates: Array.isArray(cat.templates) ? cat.templates : [],
  }));

  const pageTitle = portalContent?.pageTitle || "Free Business Legal Templates";
  const pageSubtitle = portalContent?.pageSubtitle || (
    <span>
      Access a collection of commonly used business and legal document templates—available{" "}
      <strong className="text-gray-900 dark:text-white font-semibold">
        free for BooksComply clients
      </strong>
      .
    </span>
  );
  const introParagraph =
    portalContent?.introParagraph ||
    "These templates can be downloaded directly to your computer and customized to suit your business requirements. They are designed to provide a useful starting point for common business transactions, relationships, and corporate activities.";
  const importantNotice =
    portalContent?.importantNotice ||
    "Important: These templates are provided for general informational and reference purposes only. They are not prepared specifically for your business or individual circumstances and should not be considered legal advice. Before using any template for an important or legally significant matter, we recommend reviewing and customizing it to reflect your specific situation and, where appropriate, having the final document reviewed by a qualified attorney.";

  const bottomSection = {
    title: portalContent?.bottomSection?.title ?? "A Practical Starting Point for Your Business",
    intro:
      portalContent?.bottomSection?.intro ??
      "These free templates are intended to help BooksComply clients get started with commonly used business documentation.",
    guidanceTitle:
      portalContent?.bottomSection?.guidanceTitle ??
      "Before signing, sending, or relying on any document for a significant business or legal matter:",
    checklist:
      Array.isArray(portalContent?.bottomSection?.checklist)
        ? portalContent.bottomSection.checklist
        : [
            "Customize the template to your specific circumstances.",
            "Confirm that the provisions are appropriate for your business and jurisdiction.",
            "Review applicable federal, state, and local requirements.",
            "Consider having the final document reviewed by a qualified attorney when appropriate.",
          ],
    footerNotice:
      portalContent?.bottomSection?.footerNotice ??
      "BooksComply provides these templates as a convenience to its clients and does not provide legal advice through these templates.",
  };

  const handleDownload = async (template, format) => {
    const ext = format === "pdf" ? "pdf" : "doc";
    const fileUrl = format === "pdf" ? template.pdfUrl : template.docUrl;
    const fileName =
      format === "pdf"
        ? template.pdfName || `${template.title.replace(/[^a-zA-Z0-9]/g, "_")}.pdf`
        : template.docName ||
          `${template.title.replace(/[^a-zA-Z0-9]/g, "_")}.${
            template.docUrl?.endsWith(".docx") ? "docx" : "doc"
          }`;

    if (fileUrl) {
      const fullUrl = buildPortalFileUrl(fileUrl);
      try {
        toast.success(`Starting download: ${fileName}`);
        const res = await fetch(fullUrl);
        if (!res.ok) throw new Error("File could not be fetched directly");
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        const a = document.createElement("a");
        a.href = fullUrl;
        a.download = fileName;
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      return;
    }

    // Generates a clean starting document file for client convenience
    const mime = format === "pdf" ? "application/pdf" : "application/msword";
    const content = `BooksComply Legal Template\n\nTitle: ${template.title}\nFormat: ${format.toUpperCase()}\n\n${template.description || ""}\n\nNotice: This starter template is provided for informational and reference purposes. Please customize it according to your business and legal requirements.`;

    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${template.title.replace(/[^a-zA-Z0-9]/g, "_")}_Template.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(`Downloading ${template.title} (${format.toUpperCase()})`);
  };

  const filteredCategories = resolvedCategories
    .map((catGroup) => {
      const isCategoryMatched =
        activeCategory === "All" || catGroup.category === activeCategory;
      if (!isCategoryMatched) return null;

      const matchedTemplates = catGroup.templates.filter((tmpl) => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return true;
        return (
          tmpl.title.toLowerCase().includes(q) ||
          tmpl.description.toLowerCase().includes(q)
        );
      });

      if (matchedTemplates.length === 0) return null;

      return {
        ...catGroup,
        templates: matchedTemplates,
      };
    })
    .filter(Boolean);

  const totalTemplatesCount = resolvedCategories.reduce(
    (sum, c) => sum + c.templates.length,
    0
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16 animate-fade-in-up">
      {/* 1. Page Header */}
      <PageHeader
        icon={LuFileText}
        title={pageTitle}
        subtitle={pageSubtitle}
      />

      {/* 2. Intro Description & Important Notice Card */}
      <div
        className="card p-5 sm:p-7 space-y-5 opacity-0 animate-fade-in-up"
        style={{ animationDelay: "50ms", animationFillMode: "forwards" }}
      >
        <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed font-normal">
          {introParagraph}
        </p>

        {/* Important Notice */}
        <div
          className="rounded-2xl p-4 sm:p-5 border-l-4 flex items-start gap-3.5 bg-amber-50/70 dark:bg-amber-950/20 border-gray-200 dark:border-dark-700"
          style={{ borderLeftColor: "var(--accent-500, #f97316)" }}
        >
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
            style={{
              background: "linear-gradient(135deg, rgba(249, 115, 22, 0.2), rgba(234, 88, 12, 0.3))",
              color: "var(--accent-600, #ea580c)"
            }}
          >
            <LuCircleAlert className="w-5 h-5" />
          </div>
          <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {typeof importantNotice === 'string' && importantNotice.startsWith("Important:") ? (
              <>
                <strong className="font-bold text-gray-900 dark:text-white">Important: </strong>
                {importantNotice.replace(/^Important:\s*/, "")}
              </>
            ) : (
              importantNotice
            )}
          </p>
        </div>
      </div>

      {/* 3. Search and Category Filter Card (Properly constrained within container) */}
      <div
        className="card p-4 sm:p-5 space-y-3.5 opacity-0 animate-fade-in-up w-full max-w-full overflow-hidden"
        style={{ animationDelay: "100ms", animationFillMode: "forwards" }}
      >
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Filter by Category:
            </span>
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
              {filteredCategories.reduce((acc, c) => acc + c.templates.length, 0)} Templates
            </span>
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-72 shrink-0">
            <LuSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search all templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-8 py-2 text-xs sm:text-sm rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all shadow-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <FaTimes className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Category Pills (Wrap cleanly without ever overflowing the right boundary) */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100 dark:border-dark-700/60">
          <button
            onClick={() => setActiveCategory("All")}
            className={`px-3.5 py-1.5 rounded-xl text-xs sm:text-sm font-medium transition-all ${
              activeCategory === "All"
                ? "bg-primary-600 text-white shadow-sm font-semibold"
                : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-dark-600"
            }`}
          >
            All Templates ({totalTemplatesCount})
          </button>
          {TEMPLATE_CATEGORIES.map((catGroup) => (
            <button
              key={catGroup.category}
              onClick={() => setActiveCategory(catGroup.category)}
              className={`px-3.5 py-1.5 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                activeCategory === catGroup.category
                  ? "bg-primary-600 text-white shadow-sm font-semibold"
                  : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-dark-600"
              }`}
            >
              {catGroup.category} ({catGroup.templates.length})
            </button>
          ))}
        </div>
      </div>

      {/* 4. Categorized Templates List */}
      <div
        className="space-y-8 opacity-0 animate-fade-in-up"
        style={{ animationDelay: "150ms", animationFillMode: "forwards" }}
      >
        {filteredCategories.length === 0 ? (
          <div className="card p-12 text-center">
            <LuFileText className="w-12 h-12 text-gray-400 mx-auto mb-3 opacity-60" />
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
              No templates found
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Try adjusting your search query or selecting a different category.
            </p>
          </div>
        ) : (
          filteredCategories.map((categoryGroup) => {
            const CategoryIcon = categoryGroup.icon;
            return (
              <div key={categoryGroup.category} className="space-y-3.5">
                {/* Category Header */}
                <div className="flex items-center gap-3 pb-1 border-b border-gray-100 dark:border-dark-700">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 shadow-sm"
                    style={{
                      background: "linear-gradient(135deg, var(--primary-500), var(--primary-600))",
                      color: "white"
                    }}
                  >
                    <CategoryIcon className="w-4 h-4" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white tracking-tight">
                    {categoryGroup.category}
                  </h2>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 ml-auto sm:ml-0">
                    {categoryGroup.templates.length} {categoryGroup.templates.length === 1 ? "Template" : "Templates"}
                  </span>
                </div>

                {/* Templates Grid / Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {categoryGroup.templates.map((template) => (
                    <div
                      key={template.id}
                      className="card p-5 sm:p-6 flex flex-col justify-between transition-all duration-300 hover:shadow-lg border border-gray-200/90 dark:border-dark-700 hover:border-primary-400 dark:hover:border-primary-600"
                    >
                      {/* Title & Description */}
                      <div className="space-y-2.5">
                        <div className="flex items-center gap-2">
                          <LuFileText className="w-4 h-4 text-primary-500 shrink-0" />
                          <h3 className="text-base font-bold text-gray-900 dark:text-white tracking-tight">
                            {template.title}
                          </h3>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed font-normal">
                          {template.description}
                        </p>

                        {/* Optional specialized note (e.g. for 83(b) Election Form) */}
                        {template.note && (
                          <p className="text-xs font-bold text-gray-900 dark:text-white leading-relaxed pt-1">
                            {template.note}
                          </p>
                        )}
                      </div>

                      {/* Action Buttons: Download PDF & Download DOC */}
                      <div className="mt-5 pt-4 border-t border-gray-100 dark:border-dark-700/80 flex flex-wrap items-center gap-2.5">
                        <button
                          onClick={() => handleDownload(template, "pdf")}
                          className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 text-white shadow-sm hover:scale-[1.02] active:scale-95"
                          style={{
                            background: "linear-gradient(135deg, #ef4444, #dc2626)",
                            boxShadow: "0 4px 10px -2px rgba(239, 68, 68, 0.3)"
                          }}
                        >
                          <FaFilePdf className="w-3.5 h-3.5" />
                          Download PDF
                        </button>

                        <button
                          onClick={() => handleDownload(template, "doc")}
                          className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 text-white shadow-sm hover:scale-[1.02] active:scale-95"
                          style={{
                            background: "linear-gradient(135deg, var(--primary-600), var(--primary-500))",
                            boxShadow: "0 4px 10px -2px var(--glow-color)"
                          }}
                        >
                          <FaFileWord className="w-3.5 h-3.5" />
                          Download Doc
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 5. Bottom Section: A Practical Starting Point for Your Business */}
      <div
        className="card p-6 sm:p-8 space-y-4 opacity-0 animate-fade-in-up border-t-4"
        style={{
          animationDelay: "200ms",
          animationFillMode: "forwards",
          borderTopColor: "var(--primary-500, #3b82f6)"
        }}
      >
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white tracking-tight">
          {bottomSection.title}
        </h2>

        <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-normal">
          {bottomSection.intro}
        </p>

        <p className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200 pt-1">
          {bottomSection.guidanceTitle}
        </p>

        <ul className="space-y-2 text-xs sm:text-sm text-gray-600 dark:text-gray-300 pl-1">
          {bottomSection.checklist.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        {bottomSection.footerNotice && (
          <div className="pt-3 border-t border-gray-100 dark:border-dark-700">
            <p className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
              {bottomSection.footerNotice}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessContractTemplates;
